/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Button, CircularProgress, Dialog, DialogContent, Divider, Stack } from '@mui/material'
import { useState } from 'react'

import { ContentBulkImportEditModal } from './modal/ContentBulkImportEditModal'
import { HeaderBulkImportEditModal } from './modal/HeaderBulkImportEditModal'

import { useBulkImportStore } from '@/components/BulkImportModal/hooks/useBulkImportStore'
import { bulkImportStoreActions } from '@/components/BulkImportModal/store/bulk-import.store'
import { AIButton } from '@/components/AIButton'
import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { handleUpdateAICalls } from '@/components/BulkImportModal/utils/handle-update-ai-cells'

export function BulkEditModalImportBuilder() {
  const open = useBulkImportStore(store => store.openModalBulk)
  const bulkType = useBulkImportStore(store => store.modalBulkType)
  const selectedCards = useBulkImportStore(store => store.selectedCards)
  const settings = useBulkImportStore(store => store.settings)
  const cards = useBulkImportStore(store => store.cards)
  const isLoading = useBulkImportStore(store => store.modalBulkIsLoading)

  const [dataToUpdate, setDataToUpdate] = useState<{
    field: string
    updates: any
  }>({
    field: '',
    updates: null,
  })

  const isAIButton =
    dataToUpdate.field === 'grading_method' ||
    dataToUpdate.field === 'correct_answer' ||
    bulkType === 'select_grading_method' ||
    bulkType === 'generate_translations' ||
    bulkType === 'generate_options' ||
    bulkType === 'generate_correct_answers'

  const handleChangeField = (field: string) => {
    setDataToUpdate({
      ...dataToUpdate,
      field,
    })
  }

  const handleChangeUpdates = (updates: any) => {
    setDataToUpdate({
      field: dataToUpdate.field,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      updates: updates,
    })
  }

  const handleClose = () => {
    if (isLoading) return

    bulkImportStoreActions.updateImportBulkStore({
      openModalBulk: false,
      modalBulkType: null,
    })
    setDataToUpdate({
      field: '',
      updates: null,
    })
  }

  const handleSave = () => {
    if (dataToUpdate.field === 'imageUrl') {
      const cardsToUpdate = selectedCards.map(cardId => ({
        cardId,
        fieldToUpdate: 'image.url' as const,
        updates: dataToUpdate.updates === undefined ? null : dataToUpdate.updates,
      }))

      bulkImportStoreActions.handleUpdateFieldTable(cardsToUpdate)
    }

    if (bulkType === 'bulk_edit' && dataToUpdate.field !== 'grading_method') {
      const cardsToUpdate = selectedCards.map(cardId => ({
        cardId,
        fieldToUpdate: dataToUpdate.field as keyof CardActivityWithId,
        updates: dataToUpdate.updates,
      }))

      bulkImportStoreActions.handleUpdateFieldTable(cardsToUpdate)
    }

    handleClose()
  }
  const handleSaveAICalls = async () => {
    try {
      bulkImportStoreActions.updateImportBulkStore({
        modalBulkIsLoading: true,
      })

      if (bulkType === 'select_grading_method' || dataToUpdate.field === 'grading_method') {
        const cardsToUpdate = selectedCards.map(cardId => ({
          cardId,
          fieldToUpdate: 'grading_method' as const,
          updates: {
            grading_method: settings.grading.method,
            performance_expectations: settings.grading.performanceExpectations,
            studentLevel: settings.grading.studentLevel,
            gradingCriteriaWithAI: settings.grading.gradingCriteriaWithAI,
            rubricId: settings.grading.rubricId,
            points: settings.points,
            aiFocus: settings.grading.aiFocus,
            target_proficiency_level: settings.grading.target_proficiency_level,  
            standardId: settings.grading.standardId,
          },
        }))

        const updatedCards = await handleUpdateAICalls(cards, cardsToUpdate)

        bulkImportStoreActions.updateImportBulkStore({
          cards: updatedCards,
        })
      }

      if (bulkType === 'generate_translations') {
        const cardsToUpdate = selectedCards.map(cardId => ({
          cardId,
          fieldToUpdate: 'native_text' as const,
          updates: {
            language: settings.language,
            defaultLanguage: settings.defaultLanguage,
            generateTranslations: true,
          },
        }))

        const updatedCards = await handleUpdateAICalls(cards, cardsToUpdate)

        bulkImportStoreActions.updateImportBulkStore({
          cards: updatedCards,
        })
      }

      if (bulkType === 'generate_options') {
        const cardsToUpdate = selectedCards.map(cardId => ({
          cardId,
          fieldToUpdate: 'choices' as const,
          updates: undefined,
        }))

        const updatedCards = await handleUpdateAICalls(cards, cardsToUpdate)

        bulkImportStoreActions.updateImportBulkStore({
          cards: updatedCards,
        })
      }

      if (bulkType === 'generate_correct_answers' || dataToUpdate.field === 'correct_answer') {
        const cardsToUpdate = selectedCards.map(cardId => ({
          cardId,
          fieldToUpdate: 'correct_answer' as const,
          updates: undefined,
        }))

        const updatedCards = await handleUpdateAICalls(cards, cardsToUpdate)

        bulkImportStoreActions.updateImportBulkStore({
          cards: updatedCards,
        })
      }

      if (bulkType === 'add_images') {
        const cardsToUpdate = selectedCards.map(cardId => ({
          cardId,
          fieldToUpdate: 'image.url' as const,
          updates: dataToUpdate.updates,
        }))

        const updatedCards = await handleUpdateAICalls(cards, cardsToUpdate)

        bulkImportStoreActions.updateImportBulkStore({
          cards: updatedCards,
        })
      }
    } catch (error) {
      console.error('Error updating cards', error)
    } finally {
      bulkImportStoreActions.updateImportBulkStore({
        modalBulkIsLoading: false,
      })

      handleClose()
    }
  }

  const handleDelete = () => {
    bulkImportStoreActions.bulkDeleteBulk(selectedCards)
    handleClose()
  }

  return (
    <Dialog
      // fullWidth
      maxWidth="md"
      open={open}
      sx={{
        '& .MuiDialogContent-root': {
          padding: '0px',
        },
        '& .MuiDialog-paper': {
          borderRadius: '12px',
        },
      }}
      onClose={handleClose}
    >
      <DialogContent
        // sx={{
        //   width: '550px',
        // }}
      >
        <HeaderBulkImportEditModal onClose={handleClose} />
        <Divider
          sx={{
            borderColor: '#EAECF0',
          }}
        />
        <Stack
          sx={{
            padding: bulkType === 'generate_options' ? '0px' : '24px',
          }}
        >
          <ContentBulkImportEditModal
            dataToUpdate={dataToUpdate}
            handleChangeField={handleChangeField}
            handleChangeUpdates={handleChangeUpdates}
          />
        </Stack>

        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '24px',
          }}
        >
          <Button
            sx={{
              width: '150px',
            }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          {isAIButton ? (
            <AIButton loading={isLoading} onClick={handleSaveAICalls}>
              {bulkType === 'select_grading_method' || dataToUpdate.field === 'grading_method'
                ? 'Update Grading Method'
                : null}
              {bulkType === 'generate_translations' ? 'Generate Translations' : null}
              {bulkType === 'generate_options' ? 'Generate Options' : null}
              {bulkType === 'generate_correct_answers' || dataToUpdate.field === 'correct_answer'
                ? 'Generate Correct Answers'
                : null}
            </AIButton>
          ) : null}
          {!isAIButton && bulkType !== 'bulk_delete' && bulkType !== 'add_images' ? (
            <Button
              sx={{
                width: '150px',
              }}
              variant="contained"
              onClick={handleSave}
            >
              {dataToUpdate.field === 'imageUrl' ? 'Remove Images' : 'Save Changes'}
            </Button>
          ) : null}
          {bulkType === 'add_images' ? (
            <Button
              disabled={dataToUpdate.updates == undefined}
              startIcon={isLoading ? <CircularProgress color="inherit" size={16} /> : <AddImagesIcon />}
              sx={{
                width: '140px',
              }}
              variant="contained"
              onClick={handleSaveAICalls}
            >
              Add Images
            </Button>
          ) : null}

          {bulkType === 'bulk_delete' ? (
            <Button color="error" sx={{ width: '150px' }} variant="contained" onClick={handleDelete}>
              Delete
            </Button>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

function AddImagesIcon() {
  return (
    <svg fill="none" height="18" viewBox="0 0 18 18" width="18" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.35873 2.49914H5.96401C4.75047 2.49914 4.1437 2.49914 3.68019 2.73531C3.27247 2.94305 2.94098 3.27453 2.73324 3.68225C2.49707 4.14576 2.49707 4.75254 2.49707 5.96608V12.0332C2.49707 13.2468 2.49707 13.8535 2.73324 14.3171C2.94098 14.7248 3.27247 15.0563 3.68019 15.264C4.1437 15.5002 4.75047 15.5002 5.96401 15.5002H12.609C13.2807 15.5002 13.6165 15.5002 13.8921 15.4263C14.6398 15.226 15.2239 14.6419 15.4243 13.8942C15.4981 13.6186 15.4981 13.2828 15.4981 12.6111M14.0536 6.11054V1.77686M11.8867 3.9437H16.2204M7.91417 6.47168C7.91417 7.26948 7.26742 7.91624 6.46961 7.91624C5.6718 7.91624 5.02505 7.26948 5.02505 6.47168C5.02505 5.67387 5.6718 5.02712 6.46961 5.02712C7.26742 5.02712 7.91417 5.67387 7.91417 6.47168ZM11.1572 8.94053L5.04755 14.4948C4.7039 14.8072 4.53207 14.9634 4.51687 15.0987C4.5037 15.216 4.54867 15.3323 4.63732 15.4103C4.73959 15.5002 4.9718 15.5002 5.43623 15.5002H12.2161C13.2555 15.5002 13.7753 15.5002 14.1835 15.3255C14.696 15.1063 15.1043 14.698 15.3235 14.1856C15.4981 13.7773 15.4981 13.2576 15.4981 12.2181C15.4981 11.8684 15.4981 11.6935 15.4599 11.5306C15.4118 11.326 15.3197 11.1343 15.1899 10.9689C15.0866 10.8373 14.95 10.728 14.6769 10.5096L12.6565 8.89324C12.3832 8.67456 12.2465 8.56523 12.096 8.52664C11.9634 8.49263 11.8238 8.49704 11.6935 8.53934C11.5458 8.58734 11.4162 8.70507 11.1572 8.94053Z"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.44456"
      />
    </svg>
  )
}
